import { Avatar, Button, Popover } from '@affine/component';
import { FollowInvitationView } from '@affine/core/components/collaboration/follow-invitation-view';
import { FollowingStatusView } from '@affine/core/components/collaboration/following-status-view';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { MateMarkView } from '@affine/core/desktop/components/ai-island/mate-mark-view';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import {
  type WorkingAssistant,
  WorkspacePresenceStore,
} from '@affine/core/modules/organize';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { agentFollowing$ } from '@blocksuite/affine/shared/services';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo } from 'react';

import {
  type AssistantFollowStore,
  getAssistantFollowStore,
  useAssistantFollow,
} from './assistant-follow-store';
import * as styles from './assistant-presence-control.css';

/** Presses here are not the viewer taking the view back: the chat, the island, the bar. */
const FOLLOW_KEEPS = '[data-copilot-ignore],[data-assistant-follow]';

/**
 * Following the assistant, like following a moderator with the assistant as the moderator: the
 * turn that acts in the app offers it ("Mate is working", Follow); following opens every place
 * the assistant works (doc, blocks, canvas elements) and shows the bar to stop. It ends with the
 * turn, with Stop, or when the viewer presses or scrolls in the app.
 */
export function AssistantFollowing() {
  const t = useI18n();
  const workspaceId = useService(WorkspaceService).workspace.id;
  const workbench = useService(WorkbenchService).workbench;
  const presence = useService(WorkspacePresenceStore);
  const store = useMemo(
    () => getAssistantFollowStore(workspaceId),
    [workspaceId]
  );
  const { offered, followed, place } = useAssistantFollow(store);

  useEffect(() => {
    if (followed) presence.startListening(workspaceId);
  }, [followed, presence, workspaceId]);

  // A turn another window or device sent reaches this one through its stream.
  useEffect(() => {
    if (!followed || store.drives(followed)) return;
    const close = new AbortController();
    // The chat stream code loads with the first follow of a foreign turn.
    void import('@affine/core/blocksuite/ai/utils/running-answer')
      .then(({ reportRunningTurnSteps }) =>
        reportRunningTurnSteps(workspaceId, followed, close.signal)
      )
      .catch(error => {
        if (!close.signal.aborted)
          console.error('[assistant-follow] turn stream failed', error);
      });
    return () => close.abort();
  }, [followed, store, workspaceId]);

  // Only a following window lets a crewmate's playback bring its target into view.
  useEffect(() => {
    agentFollowing$.next(!!followed);
    return () => agentFollowing$.next(false);
  }, [followed]);

  useEffect(() => {
    if (!followed || !place) return;
    workbench.openDoc({
      docId: place.docId,
      ...(place.elementIds.length ? { mode: 'edgeless' as const } : {}),
      ...(place.blockIds.length ? { blockIds: [...place.blockIds] } : {}),
      ...(place.elementIds.length ? { elementIds: [...place.elementIds] } : {}),
      refreshKey: place.key,
    });
  }, [followed, place, workbench]);

  useEffect(() => {
    if (!followed) return;
    const takeBack = (event: Event) => {
      const target = event.target;
      if (
        !event.isTrusted ||
        (target instanceof Element && target.closest(FOLLOW_KEEPS))
      )
        return;
      store.detach();
    };
    document.addEventListener('pointerdown', takeBack, true);
    document.addEventListener('wheel', takeBack, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener('pointerdown', takeBack, true);
      document.removeEventListener('wheel', takeBack, true);
    };
  }, [followed, store]);

  const name = t['com.streamlain.ai.follow.name']();
  if (followed)
    return (
      <div data-assistant-follow>
        <FollowingStatusView
          status={t['com.streamlain.moderation.following.status']({ name })}
          stopLabel={t['com.streamlain.moderation.following.stop']()}
          onStop={() => store.detach()}
          testId="assistant-following-bar"
        />
      </div>
    );
  if (offered)
    return (
      <div data-assistant-follow>
        <FollowInvitationView
          icon={<MateMarkView size={24} />}
          title={t['com.streamlain.ai.follow.working']()}
          followLabel={t['com.streamlain.moderation.invitation.follow']()}
          onFollow={() => store.follow(offered)}
          onClose={() => store.dismiss()}
          testId="assistant-follow-invitation"
        />
      </div>
    );
  return null;
}

/** The assistants at work in the workspace, among the people of a doc. */
export function WorkingAssistants({ workspaceId }: { workspaceId: string }) {
  const presence = useService(WorkspacePresenceStore);
  const assistants = useLiveData(presence.assistants$);
  const store = useMemo(
    () => getAssistantFollowStore(workspaceId),
    [workspaceId]
  );
  const { followed } = useAssistantFollow(store);

  return assistants.map(assistant => (
    <WorkingAssistantAvatar
      key={assistant.sessionId}
      assistant={assistant}
      workspaceId={workspaceId}
      store={store}
      following={followed === assistant.sessionId}
    />
  ));
}

function WorkingAssistantAvatar({
  assistant,
  workspaceId,
  store,
  following,
}: {
  assistant: WorkingAssistant;
  workspaceId: string;
  store: AssistantFollowStore;
  following: boolean;
}) {
  const t = useI18n();
  const docTitle = t.t(
    useLiveData(useService(DocDisplayMetaService).title$(assistant.docId))
  );
  const { jumpToPage } = useNavigateHelper();
  const label = t['com.streamlain.ai.app-use.assistant-of']({
    name: assistant.name ?? '',
  });
  const place = t['com.streamlain.ai.watch.working-on']({ name: docTitle });

  return (
    <Popover
      contentOptions={{ align: 'end', className: styles.popover }}
      content={
        <div className={styles.panel} data-testid="working-assistant-panel">
          <span className={styles.name}>{label}</span>
          <Button
            variant="plain"
            onClick={() => jumpToPage(workspaceId, assistant.docId)}
            data-testid="working-assistant-place"
          >
            {place}
          </Button>
          {assistant.activity ? (
            <span
              className={styles.activity}
              data-testid="working-assistant-activity"
            >
              {assistant.activity}
            </span>
          ) : null}
          {following ? (
            <Button
              variant="secondary"
              onClick={() => store.detach()}
              data-testid="working-assistant-detach"
            >
              {t['com.streamlain.ai.watch.detach']()}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => store.follow(assistant.sessionId)}
              data-testid="working-assistant-follow"
            >
              {t['com.streamlain.ai.watch.follow']()}
            </Button>
          )}
        </div>
      }
    >
      <button
        type="button"
        className={styles.avatar}
        aria-label={`${label}, ${place}`}
        title={`${label}, ${place}`}
        data-working
        data-following={following}
        data-testid="working-assistant-avatar"
      >
        <Avatar
          name={label}
          size={28}
          avatarTooltipOptions={{ content: null }}
        />
      </button>
    </Popover>
  );
}
